package dev.lingvo.site.components.studio.fonts

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeoutOrNull

/** A sample binds readiness to the actual unicode subsets, weight and style being rendered. */
data class FontLoadRequest(
    val family: String, val weight: Int = 700, val italic: Boolean = false,
    val sample: String, val locale: String = "",
)

data class FontWaitPolicy(val timeoutMs: Long = 60_000, val backoffMs: Long = 1_000)

data class FontLoadProgress(
    val done: Int, val total: Int, val current: FontLoadRequest? = null, val attempt: Int = 1,
)

fun interface FontLoader {
    suspend fun load(request: FontLoadRequest, attempt: Int): Boolean
}

/** Shared by early prefetch and compose. Failed or cancelled work never poisons a later Retry. */
class FontLoading(
    private val loader: FontLoader,
    private val policy: FontWaitPolicy = FontWaitPolicy(),
    private val pause: suspend (Long) -> Unit = { delay(it) },
    private val cachedReady: (FontLoadRequest) -> Boolean = { true },
) {
    private val ready = mutableSetOf<FontLoadRequest>()
    private val pending = mutableMapOf<FontLoadRequest, CompletableDeferred<Boolean?>>()

    /** Returns the failed family, or null after every requested family and subset is ready. */
    suspend fun load(requests: List<FontLoadRequest>, progress: (FontLoadProgress) -> Unit = {}): FontLoadRequest? {
        val unique = requests.distinctBy { it.copy(locale = "") }
        unique.forEachIndexed { index, request ->
            progress(FontLoadProgress(index, unique.size, request))
            if (!ensure(request) { attempt -> progress(FontLoadProgress(index, unique.size, request, attempt)) }) return request
        }
        progress(FontLoadProgress(unique.size, unique.size))
        return null
    }

    private suspend fun ensure(request: FontLoadRequest, onAttempt: (Int) -> Unit): Boolean {
        val key = request.copy(locale = "")
        while (true) {
            if (key in ready) {
                if (cachedReady(request)) return true
                ready.remove(key)
            }
            val other = pending[key]
            if (other != null) {
                // If a screen that owned prefetch left, compose takes ownership instead of failing.
                val result = other.await()
                if (result != null) return result
                continue
            }
            val result = CompletableDeferred<Boolean?>()
            pending[key] = result
            try {
                repeat(2) { index ->
                    if (index > 0) pause(policy.backoffMs)
                    onAttempt(index + 1)
                    val loaded = withTimeoutOrNull(policy.timeoutMs) {
                        try { loader.load(request, index + 1) }
                        catch (cancelled: CancellationException) { throw cancelled }
                        catch (_: Throwable) { false }
                    } == true
                    if (loaded) {
                        ready += key
                        result.complete(true)
                        return true
                    }
                }
                result.complete(false)
                return false
            } finally {
                pending.remove(key)
                if (!result.isCompleted) result.complete(null)
            }
        }
    }
}
